<template>
  <div class="home-echarts-wrap" :style="{ width, height, marginBottom }">
    <div class="home-echarts-header" style="padding: 10px 24px 0 24px;height: auto">
      <h3 class="home-echarts-header__title">{{ title }}</h3>
      <div class="home-echarts-header__controls">
        <a-date-picker
          @change="handleDateChange"
          v-model:value="yearValue"
          :allowClear="false"
          picker="year"
        />
      </div>
    </div>
    <!-- 延迟显示 loading 效果。当 spinning 状态在 delay 时间内结束，则不显示 loading 状态。 -->
    <a-spin :spinning="spinning" :delay="200">
      <!-- echarts 图表 -->
      <div ref="chartRef" class="home-echarts-content" v-show="showECharts"></div>
      <dc-layout-empty type="nothing" v-if="!showECharts"/>
    </a-spin>
  </div>
</template>

<script setup lang="ts">
import type { PropType, Ref } from 'vue'
import { onBeforeMount, reactive, ref, watch } from 'vue'
import { useECharts } from '@/hooks/useECharts'
import dayjs, { Dayjs } from 'dayjs'
import { getNewActivatedCustomerCount } from '@/api/gucangSales/dataBoard'

interface QueryFormType {
  employeeNo?: string
  dataPermissionQueryType: number
  startDay?: string
  endDay?: string
}

interface ItemCommonType {
  monthValue?: number,
  quantity?: number, // 激活数
}

const yearValue = ref<Dayjs>(dayjs())
const props = defineProps({
  title: {
    type: String as PropType<string>,
    default: '新激活客户数'
  },
  appId: {
    type: String as PropType<'cos_fbu' | 'cos_lbu'>,
    required: true
  },
  width: {
    type: String as PropType<string>,
    default: '100%'
  },
  height: {
    type: String as PropType<string>,
    default: '350px'
  },
  marginBottom: {
    type: String as PropType<string>,
    default: '8px'
  },
  allowClear: {
    type: Boolean as PropType<boolean>,
    default: false
  },
  employeeNo: {
    type: String as PropType<string>
  }
})

const spinning = ref(false)
const showECharts = ref(false)
const startMonth = ref<string>(dayjs(yearValue.value).startOf('month').format('YYYY01'))
const endMonth = ref<string>(dayjs(yearValue.value).endOf('month').format('YYYY12'))
const xAxisData = ref<any[]>([])
const queryForm = reactive<QueryFormType>({
  startDay: dayjs(yearValue.value).startOf('year').format('YYYY/MM/DD'),
  endDay: dayjs(yearValue.value).endOf('year').format('YYYY/MM/DD'),
  dataPermissionQueryType: props.employeeNo ? 2 : 1,
  employeeNo: props.employeeNo
})
watch(() => props.employeeNo, (val) => {
  queryForm.employeeNo = val
  if (val) {
    queryForm.dataPermissionQueryType = 2
  } else {
    queryForm.dataPermissionQueryType = 1
  }
  getData()
})
const handleDateChange = () => {
  queryForm.startDay = dayjs(yearValue.value).startOf('year').format('YYYY/MM/DD')
  queryForm.endDay = dayjs(yearValue.value).endOf('year').format('YYYY/MM/DD')
  startMonth.value = dayjs(yearValue.value).startOf('month').format('YYYY01')
  endMonth.value = dayjs(yearValue.value).endOf('month').format('YYYY12')
  xAxisData.value = generateTimeArray(startMonth.value, endMonth.value, 'YYYYmm')
  getData()
}

function generateTimeArray(startTime: string, endTime: string, format: 'YYYYmm' | 'YYYYww' | 'YYYY/MM/DD') {
  const start = dayjs(startTime, format)
  const end = dayjs(endTime, format)
  const result = []

  let current = start

  if (format === 'YYYYmm') {
    while (current.isBefore(end) || current.isSame(end)) {
      result.push({
        value: current.format(format),
        name: current.format('m') + '月'
      })
      current = current.add(1, 'm')
    }
  } else if (format === 'YYYY/MM/DD') {
    while (current.isBefore(end) || current.isSame(end)) {
      result.push({
        value: current.format(format),
        name: current.format('D')
      })
      current = current.add(1, 'day')
    }
  } else {
    throw new Error('Unsupported format')
  }
  return result
}

const getData = () => {
  spinning.value = true
  getNewActivatedCustomerCount(queryForm).then((res) => {
    const activatedCustomerData = res.data as unknown as ItemCommonType[]
    const activatedCustomerList = transChartData(activatedCustomerData)
    if (activatedCustomerList.length) {
      showECharts.value = true
      initChart(activatedCustomerList)
    } else {
      showECharts.value = false
      initChart([])
    }
  }).finally(() => {
    spinning.value = false
  })
}
/// 格式化接口数据和日期对应
const transChartData = (serviceData: ItemCommonType[]) => {
  const matchData: number[] = []
  if (serviceData && serviceData.length > 0) {
    xAxisData.value.forEach((axis) => {
      const matchItem = serviceData.find((item) => {
        return (item.monthValue ?? 0).toString() === axis.value
      })
      if (matchItem) {
        matchData.push(matchItem.quantity ?? 0)
      } else {
        matchData.push(0)
      }
    })
  }
  return matchData
}
const chartRef = ref<HTMLDivElement | null>(null)
const { setOptions } = useECharts(chartRef as Ref<HTMLDivElement>, undefined, false)
const initChart = (newActivatedCustomerCountList: number[]) => {
  setOptions({
    // 悬浮窗
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#1D4CD2',
      textStyle: {
        color: 'White'
      },
      formatter: function (params) {
        const currentData = xAxisData.value[params[0].dataIndex].value
        const toolsTipsResult = []
        toolsTipsResult.push(currentData)
        for (let i = 0; i < params.length; i++) {
          const currentParam = params[i]
          toolsTipsResult.push(currentParam.seriesName + '  <strong>' + (i === 2 ? currentParam.value + '%' : currentParam.value) + '</strong>')
          // toolsTipsResult.push('目标值  <strong>' + currentHoverData?.quantity + '</strong>')
          // toolsTipsResult.push('完成值  <strong>' + currentHoverData?.total + '</strong>')
          // toolsTipsResult.push(`完成率  <strong>${(currentHoverData?.total ? Number((((currentHoverData.quantity ?? 0) / currentHoverData.total) * 100).toFixed(2)) : 0)}%` + '</strong>')
        }
        return toolsTipsResult.join('<br>')
      }
    },
    /// 在这里配置线条颜色和legend颜色一致
    color: '#1D4CD2',
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: xAxisData.value.map((item) => item.name)
    },
    yAxis: {
      type: 'value',
      splitLine: {
        show: true,
        lineStyle: {
          color: '#D8DFE4',
          type: 'dashed'
        }
      }
    },
    series: [
      {
        name: '激活数',
        type: 'line',
        smooth: true,
        data: newActivatedCustomerCountList
      }
    ]
  })
}
onBeforeMount(() => {
  if (!queryForm.startDay) {
    queryForm.startDay = dayjs(yearValue.value).startOf('year').format('YYYY/MM/DD')
  }
  if (!queryForm.endDay) {
    queryForm.endDay = dayjs(yearValue.value).endOf('year').format('YYYY/MM/DD')
  }
  xAxisData.value = generateTimeArray(startMonth.value, endMonth.value, 'YYYYmm')
  getData()
})
</script>
<script lang="ts">
export default {
  name: 'NewActivatedCustomerCount'
}
</script>
<style scoped lang="scss">

</style>
